'use client';
import { useState } from 'react';
import { Center, HStack, Text, VStack } from '@chakra-ui/react';

import SliderControls from 'features/SliderControls';
import ProjectCard from 'entities/ProjectCard';
import { scrollBarStyle } from 'shared/TextField/model/scrollBarStyle';
import ProjectDescription from 'shared/ProjectDescription';
import { useSwiperControls } from 'shared/hooks';
import NextImage from 'shared/NextImage';
import Slider from 'shared/Slider';
import Tag from 'shared/Tag';

import { modalLayoutConfig } from 'constants/Projects';
import { DEFAULT_BORDER, EMPTY_OBJECT } from 'constants/global';
import { getSlidesData, projectDescriptionCardStyle } from './model';

export default function ProjectInfo({
  projectInfo: {
    name,
    modalTaskDescription,
    modalTechnologies,
    modalTeam,
    modalDuration,
    modalIndustry,
    modalSystems,
    is1C = false,
    ...other
  },
  onChangeProject,
}) {
  const [swiper, setSwiper] = useState();
  const swiperControls = useSwiperControls(swiper);

  return (
    <VStack
      gap={{ mobile: '32px', tablet: '40px', laptop: '60px' }}
      overflow="auto"
      sx={scrollBarStyle}
    >
      <VStack
        w="full"
        pos="relative"
        gap={{ mobile: '20px', tablet: '32px', laptop: '48px' }}
        border={DEFAULT_BORDER}
        borderColor="line.gray"
        borderRadius={{ mobile: '20px', tablet: '24px', laptop: '48px' }}
      >
        <ProjectCard
          h="440px"
          maxH={{ tablet: '186px', laptop: '336px' }}
          borderRadius={{ mobile: '20px', tablet: '24px', laptop: '48px' }}
          _hover={EMPTY_OBJECT}
          {...other}
          {...modalLayoutConfig[name]}
        />

        <VStack
          w="full"
          gap={{ mobile: '20px', tablet: '40px', laptop: '60px' }}
          px={{ mobile: '16px', tablet: '20px', laptop: '24px' }}
          pb={{ mobile: '16px', tablet: '20px', laptop: '24px' }}
        >
          <HStack
            flexDirection={{ mobile: 'column', laptop: 'row' }}
            w="full"
            align="start"
            justifyContent="space-between"
            gap={{ mobile: '20px', tablet: '32px', laptop: '46px' }}
          >
            <VStack
              gap={{ mobile: '12px', tablet: '16px', laptop: '20px' }}
              align="start"
              flexGrow="1"
            >
              <Text textStyle="header.h4" fontWeight="500">
                Описание проекта
              </Text>

              <Text
                textStyle="h1"
                fontWeight="500"
                dangerouslySetInnerHTML={{ __html: modalTaskDescription }}
                maxW="99%"
              />
            </VStack>

            <VStack
              w="full"
              minW={{ tablet: '380px', xxl: '560px' }}
              gap={{ mobile: '20px', tablet: '32px', laptop: '48px' }}
              align="start"
            >
              {modalTechnologies && (
                <VStack gap={{ mobile: '12px', tablet: '16px', laptop: '20px' }} align="start">
                  <Text textStyle="header.h4" fontWeight="500">
                    Технологии
                  </Text>

                  <HStack flexWrap="wrap">
                    {modalTechnologies.map(technology => (
                      <Tag
                        key={technology}
                        p={{ laptop: '7.5px 16px', mobile: '6px 12px' }}
                        color="text.black"
                        fontSize={{ laptop: '18px', mobile: '12px' }}
                        lineHeight="140%"
                        fontWeight="500"
                      >
                        {technology}
                      </Tag>
                    ))}
                  </HStack>
                </VStack>
              )}

              <HStack w="full" justifyContent="space-between" flexWrap="wrap" align="start">
                <VStack
                  gap={{ mobile: '12px', tablet: '16px', laptop: '24px' }}
                  align="start"
                  minW="fit-content"
                >
                  <Text textStyle="header.h4" fontWeight="500">
                    Команда
                  </Text>

                  <Text textStyle="h1" fontWeight="500">
                    {modalTeam}
                  </Text>
                </VStack>

                <VStack
                  gap={{ mobile: '12px', tablet: '16px', laptop: '24px' }}
                  align="start"
                  maxW={{ laptop: '215px' }}
                >
                  <Text textStyle="header.h4" fontWeight="500">
                    Длительность
                  </Text>

                  <Text textStyle="h1" fontWeight="500">
                    {modalDuration}
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </HStack>

          {modalIndustry && (
            <HStack
              flexDirection={{ mobile: 'column', tablet: 'row' }}
              gap={{ mobile: '16px', tablet: 'none' }}
              align="start"
              w="full"
              justifyContent="space-between"
            >
              <Tag as="div" bgColor="bg.gray" border="none" p="none">
                <HStack p="4px 32px 4px 4px" gap="20px">
                  <Center
                    boxSize={{ mobile: '32px', tablet: '40px', laptop: '60px', desktop: '72px' }}
                    borderRadius="50%"
                    bgColor="bg.white"
                  >
                    <NextImage
                      src={modalIndustry?.icon}
                      alt="icon"
                      w="auto"
                      h="auto"
                      boxSize={{ laptop: '32px', mobile: '24px' }}
                    />
                  </Center>

                  <Text textStyle="h1" fontWeight="500">
                    {modalIndustry.text}
                  </Text>
                </HStack>
              </Tag>

              <HStack
                gap={{ mobile: '8px', laptop: '20px' }}
                flexWrap="wrap"
                justifyContent={{ mobile: 'start', tablet: 'end' }}
              >
                {modalSystems?.map(({ icon, text }) => (
                  <Tag key={text} as="div" bgColor="bg.white" border="1px solid #EAEFE8" p="none">
                    <HStack p="4px 32px 4px 4px" gap="16px">
                      <Center
                        boxSize={{
                          mobile: '32px',
                          tablet: '40px',
                          laptop: '60px',
                          desktop: '72px',
                        }}
                        borderRadius="50%"
                        bgColor="bg.neonLight"
                      >
                        <NextImage
                          src={icon}
                          alt="icon"
                          w="auto"
                          h="auto"
                          boxSize={{ laptop: '32px', mobile: '24px' }}
                        />
                      </Center>

                      <Text textStyle="h1" fontWeight="500">
                        {text}
                      </Text>
                    </HStack>
                  </Tag>
                ))}
              </HStack>
            </HStack>
          )}
        </VStack>
      </VStack>

      <VStack w="full" gap={{ mobile: '12px', tablet: '16px', laptop: '24px' }}>
        <HStack w="full" justifyContent="space-between">
          <Text textStyle="header.h4" fontWeight="500">
            Другие проекты
          </Text>

          <SliderControls
            {...swiperControls}
            hideBelow="none"
            iconProps={{ boxSize: { mobile: '40px', laptop: '64px' } }}
          />
        </HStack>

        <Slider
          controller={swiper}
          setController={setSwiper}
          slidesData={getSlidesData(name, onChangeProject, is1C)}
          SlideComponent={ProjectDescription}
          sameStyles={projectDescriptionCardStyle}
          slidesPerView="auto"
          spaceBetween="0"
        />
      </VStack>
    </VStack>
  );
}
